<template>
  <div class="home-luxury">
    <!-- ⭐ القسم الرئيسي Hero Section -->
    <section class="hero-section relative">
      <div class="hero-overlay"></div>
      <div class="container text-center hero-content position-relative z-1 animate-slide-up">
        <h1 class="hero-title display-3 fw-bold text-shadow-glow mb-4">{{ settings.siteName || 'الرحيمة' }}</h1>
        <p class="hero-subtitle lead text-theme-muted mx-auto mb-5" style="max-width: 800px; line-height: 1.8;">
          {{ settings.siteDescription || 'نرتقي معكم بتعليم القرآن الكريم والمحتوى الهادف بأسلوب بسيط مع الإلتزام بالهوية الإسلامية.' }}
        </p>
        <div class="hero-actions d-flex justify-content-center gap-3">
          <button class="btn btn-gold px-5 py-3 rounded-pill fw-bold d-flex align-items-center gap-2" @click="scrollToSection('features')">
            <i class="bi bi-arrow-down-circle fs-5"></i> اكتشف المزيد
          </button>
          <button class="btn btn-glass-outline px-5 py-3 rounded-pill fw-bold d-flex align-items-center gap-2" @click="navigateToQuranSection">
            <i class="bi bi-play-circle fs-5 text-gold"></i> استمع الآن
          </button>
        </div>
      </div>
    </section>


    <!-- ⭐ قسم الميزات -->
    <section class="features container py-5" id="features">
      <div class="text-center mb-5 animate-slide-up">
         <h2 class="section-title text-gold fw-bold position-relative d-inline-block">مميزات المنصة</h2>
      </div>
      <div class="row g-4 justify-content-center">
        <div class="col-md-6 col-lg-4" v-for="(feature, index) in features" :key="index">
          <div class="feature-card glass-card text-center p-5 h-100 rounded-4 position-relative overflow-hidden animate-slide-up" :style="{ animationDelay: `${index * 0.1}s` }">
            <div class="card-glow"></div>
            <div class="position-relative z-1">
              <div class="feature-icon-wrapper mx-auto mb-4 bg-white bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style="width: 80px; height: 80px; border: 1px solid rgba(255,215,0,0.3);">
                <i :class="[feature.icon, 'fs-1 text-gold']"></i>
              </div>
              <h4 class="feature-title fw-bold text-theme-base mb-3">{{ feature.title }}</h4>
              <p class="text-theme-muted mb-4" style="line-height: 1.6;">{{ feature.description }}</p>
              <router-link :to="feature.link" class="feature-link text-gold text-decoration-none fw-bold d-inline-flex align-items-center gap-2 hover-white">
                اكتشف المزيد <i class="bi bi-arrow-left"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>



    <!-- ⭐ قسم الدورات التدريبية المتاحة -->
    <section v-if="availableCourses.length > 0" class="courses-section py-5 relative" id="courses-section">
      <div class="container position-relative z-1 animate-slide-up">
        <div class="text-center mb-5">
           <h2 class="section-title text-gold fw-bold position-relative d-inline-block">الدورات التدريبية المتاحة</h2>
           <p class="text-theme-muted mt-2">اشترك الآن في دوراتنا التعليمية المتميزة مع نخبة من المدرسين</p>
        </div>
        
        <div class="row g-4 justify-content-center">
          <div class="col-md-6 col-lg-4" v-for="course in availableCourses" :key="course.id">
            <div class="course-card-premium glass-card rounded-4 overflow-hidden position-relative h-100 hover-scale">
              <div class="course-badge-status position-absolute top-0 start-0 m-3 z-3">
                <span class="badge bg-gold text-dark fw-bold rounded-pill px-3 py-2 shadow-sm">مفتوح الآن</span>
              </div>
              <div class="course-img-wrapper position-relative" style="height: 220px;">
                <img :src="getFullImageUrl(course.image)" class="w-100 h-100" style="object-fit: cover;">
                <div class="course-overlay-grad"></div>
                <div class="course-price-tag position-absolute bottom-0 end-0 m-3 z-3">
                  <span class="fs-4 fw-bold text-gold">{{ course.price }} <small class="fs-6">ج.س</small></span>
                </div>
              </div>
              
              <div class="course-body p-4 text-end">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span class="badge glass-badge text-primary extra-small fw-bold">{{ course.category }}</span>
                  <span class="text-theme-muted extra-small d-flex align-items-center gap-1">
                    <i class="bi bi-person-check text-gold"></i> {{ course.instructor }}
                  </span>
                </div>
                
                <h4 class="course-title h5 fw-bold text-theme-base mb-3" style="min-height: 48px;">{{ course.title }}</h4>
                
                <div class="course-details-grid d-grid gap-2 mb-4">
                  <div class="d-flex align-items-center gap-2 text-theme-muted small">
                    <i class="bi bi-calendar3 text-gold"></i>
                    <span>البداية: <b>{{ course.start_date || 'قريباً' }}</b></span>
                  </div>
                  <div class="d-flex align-items-center gap-2 text-theme-muted small">
                    <i class="bi bi-clock text-gold"></i>
                    <span>الموعد: <b>{{ course.class_time || 'يحدد لاحقاً' }}</b></span>
                  </div>
                  <div class="d-flex align-items-center gap-2 text-theme-muted small">
                    <i class="bi bi-geo-alt text-gold"></i>
                    <span>نوع الحضور: <b>{{ course.attendance_type || 'أونلاين' }}</b></span>
                  </div>
                </div>
                
                <router-link to="/courses" class="btn btn-gold w-100 py-2 rounded-pill fw-bold shadow-gold-sm d-flex align-items-center justify-content-center gap-2">
                  <i class="bi bi-pencil-square"></i> اشترك الآن
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ⭐ قسم المقالات الإسلامية -->
    <section class="articles-section py-5 relative" id="articles-section">
      <div class="container position-relative z-1 animate-slide-up">
        <div class="text-center mb-5">
           <h2 class="section-title text-gold fw-bold position-relative d-inline-block">مقالات إسلامية</h2>
           <p class="text-theme-muted mt-2">مقالات متنوعة في الفقه، السيرة النبوية، شرح الأحاديث، والأذكار</p>
        </div>
        
        <div class="row g-4 justify-content-center" dir="rtl">
          <div class="col-md-6 col-lg-4" v-for="article in latestArticles" :key="article.id">
            <div class="article-card-premium glass-card rounded-4 overflow-hidden position-relative h-100 hover-scale">
              <div class="article-category-badge position-absolute top-0 start-0 m-3 z-3">
                <span class="badge glass-badge text-gold fw-bold rounded-pill px-3 py-2 shadow-sm">{{ article.category }}</span>
              </div>
              <div class="article-img-wrapper position-relative" style="height: 200px; background: rgba(255,215,0,0.05);">
                <img v-if="article.thumbnail" :src="getFullImageUrl(article.thumbnail)" class="w-100 h-100" style="object-fit: cover;" loading="lazy">
                <div v-else class="w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                   <i class="bi bi-journal-richtext text-gold opacity-25" style="font-size: 5rem;"></i>
                </div>
                <div class="article-overlay-grad"></div>
              </div>
              
              <div class="article-body p-4 text-end" dir="rtl">
                <h4 class="article-title h5 fw-bold text-theme-base mb-2" style="min-height: 45px; line-height: 1.4; text-align: right !important;">{{ article.title }}</h4>
                <p class="article-snippet text-theme-muted mb-4 small" style="display: -webkit-box; -webkit-line-clamp: 6; line-clamp: 6; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.8; text-align: right !important;">
                  {{ article.content.replace(/<[^>]*>/g, '').substring(0, 350) }}
                </p>
                
                <div class="d-flex justify-content-between align-items-center mt-auto pt-3 border-top border-light border-opacity-10">
                   <span class="text-theme-muted extra-small d-flex align-items-center gap-1">
                     <i class="bi bi-eye text-gold"></i> {{ article.views }}
                   </span>
                   <router-link :to="`/articles/${article.id}`" class="btn-link text-gold text-decoration-none fw-bold d-flex align-items-center gap-2">
                     إقرأ المزيد <i class="bi bi-arrow-left"></i>
                   </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center mt-5">
           <router-link to="/articles" class="btn btn-glass-outline rounded-pill px-5 py-2 fw-bold">تصفح كافة المقالات</router-link>
        </div>
      </div>
    </section>

    <!-- ⭐ قسم الإحصائيات -->
    <section class="stats-section py-5 relative">
      <div class="container position-relative z-1 animate-slide-up">
        <div class="row text-center g-4 justify-content-center">
          <div class="col-6 col-md-3 stat-item" v-for="stat in statistics" :key="stat.id">
            <div class="stat-card glass-card p-4 rounded-4 h-100 d-flex flex-column justify-content-center align-items-center hover-scale border border-light border-opacity-10">
              <div class="stat-icon mb-3 bg-white bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style="width: 70px; height: 70px; border: 1px solid rgba(255,215,0,0.2);">
                <i :class="[stat.icon, 'fs-2 text-gold']"></i>
              </div>
              <h3 class="stat-number fw-bold text-gold display-5 mb-1 text-shadow-glow">{{ stat.value }}</h3>
              <p class="stat-label text-theme-muted mb-0 fw-medium">{{ stat.label }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ⭐ قسم المناسبات -->
    <section class="events-section py-5 relative">
      <div class="container position-relative z-1 animate-slide-up">
        <div class="section-header text-center mb-5">
          <h2 class="section-title text-gold fw-bold position-relative d-inline-block">المناسبات الإسلامية</h2>
        </div>

        <div v-if="userEvents.length === 0" class="empty-events glass-panel p-5 rounded-4 text-center border border-light border-opacity-10 max-w-lg mx-auto">
          <div class="empty-icon text-gold mb-3 opacity-75 display-4">
             <i class="bi bi-calendar-event"></i>
          </div>
          <h4 class="text-theme-base mb-2">لا توجد مناسبات حالياً</h4>
          <p class="text-theme-muted">ترقبوا قريباً مجموعة من المناسبات والفعاليات الإسلامية المميزة عبر منصتنا</p>
        </div>

        <div v-else class="events-timeline mx-auto" style="max-width: 800px;">
          <div class="event-item glass-card p-4 rounded-4 mb-4 d-flex flex-column flex-md-row gap-4 align-items-md-center position-relative overflow-hidden hover-scale" v-for="event in userEvents" :key="event.id">
            <div class="card-glow"></div>
            <div class="event-date text-center p-3 rounded-4 bg-white bg-opacity-10 border border-gold border-opacity-25" style="min-width: 120px;">
              <span class="event-day d-block fs-1 fw-bold text-gold lh-1 mb-1">{{ event.day }}</span>
              <span class="event-month fs-5 text-theme-base">{{ event.month }}</span>
            </div>
            <div class="event-content flex-grow-1 position-relative z-1">
              <h5 class="text-theme-base fs-4 fw-bold mb-2">{{ event.title }}</h5>
              <p class="text-theme-muted mb-3" style="line-height: 1.6;">{{ event.description }}</p>
              <div class="event-meta d-flex flex-wrap gap-3 text-gold opacity-75 small fw-medium">
                <span class="d-flex align-items-center gap-1"><i class="bi bi-clock"></i> {{ event.time }}</span>
                <span class="online-badge px-3 py-1 bg-success bg-opacity-10 text-success rounded-pill d-flex align-items-center gap-1" :class="{ 'bg-primary text-primary': !event.isOnline }">
                  <i :class="event.isOnline ? 'bi bi-laptop' : 'bi bi-geo-alt'"></i>
                  {{ event.isOnline ? 'أونلاين' : 'حضوري' }}
                </span>
                <span v-if="event.location && !event.isOnline" class="d-flex align-items-center gap-1 text-theme-muted">
                  <i class="bi bi-pin-map text-gold"></i> {{ event.location }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ⭐ قسم الصلوات -->
    <section class="prayer-times py-5 relative">
      <div class="container position-relative z-1 animate-slide-up">
        <div class="section-header d-flex flex-column flex-md-row justify-content-between align-items-center mb-5 pb-3 border-bottom border-light border-opacity-10">
          <h2 class="section-title text-gold fw-bold m-0 position-relative">أوقات الصلاة</h2>
          <button class="btn btn-glass-outline rounded-pill px-4 mt-3 mt-md-0 fw-bold d-flex align-items-center gap-2" @click="showLocationModal = true">
            <i class="bi bi-geo-alt-fill text-gold"></i> تغيير الموقع
          </button>
        </div>

        <div class="location-info glass-panel rounded-4 py-3 px-3 px-md-4 d-flex mx-auto flex-column flex-md-row align-items-center justify-content-center gap-2 gap-md-3 mb-5 text-center">
          <span class="text-theme-base d-flex align-items-center justify-content-center gap-2 fw-medium">
            <i class="bi bi-pin-map-fill text-gold fs-5"></i>
            {{ currentLocation.city }}, {{ currentLocation.country }}
          </span>
          <span class="text-theme-muted d-none d-md-block">|</span>
          <span v-if="nextPrayer" class="next-prayer-badge bg-gold text-dark px-3 py-2 px-md-4 rounded-pill fw-bold mt-2 mt-md-0">
            الصلاة القادمة: {{ nextPrayer.name }} بعد {{ nextPrayer.remaining }}
          </span>
        </div>

        <div class="prayer-grid row g-3 justify-content-center">
          <div class="col-6 col-md-4 col-lg-2" v-for="prayer in prayerTimes" :key="prayer.name">
            <div class="prayer-card glass-card rounded-4 h-100 position-relative transition-all overflow-hidden" 
                 :class="{ 'active-prayer': prayer.isNext }">
              <div class="active-indicator" v-if="prayer.isNext"></div>
              <div class="prayer-card-inner p-3 p-md-4 d-flex flex-column align-items-center justify-content-center gap-2">
                <div class="prayer-icon-container mb-2" :class="{ 'active': prayer.isNext }">
                  <i :class="[prayer.icon, 'fs-3']"></i>
                </div>
                <div class="prayer-info text-center">
                  <h6 class="prayer-name fw-bold mb-1" :class="prayer.isNext ? 'text-gold' : 'text-theme-muted'">{{ prayer.name }}</h6>
                  <div class="prayer-time fs-4 fw-bold font-monospace text-theme-base">{{ prayer.time }}</div>
                  <div v-if="prayer.isNext" class="next-label mt-2 animate-pulse">
                    <span class="badge bg-gold text-dark rounded-pill px-3 py-1 small fw-bold">الآن</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 🌙 قسم رمضان (الإمساك والإفطار) -->
        <div v-if="settings.showRamadanMode && !settings.maintenanceMode" class="ramadan-section mt-5 animate-slide-up">
          <div class="ramadan-card glass-card p-4 rounded-4 border-gold position-relative overflow-hidden text-center text-theme-base z-1">
            <div class="card-glow active"></div>
            <div class="row align-items-center justify-content-center position-relative z-1">
              <div class="col-md-4 mb-4 mb-md-0 d-flex flex-column align-items-center">
                <i class="bi bi-moon-stars-fill text-gold display-4 mb-2 animate-pulse"></i>
                <h3 class="fw-bold mb-1 text-theme-base">رمضـان مـبــارك</h3>
                <p class="text-theme-muted mb-0">مواقيت الإمساك والإفطار اليوم</p>
              </div>
              <div class="col-md-4 col-sm-6 mb-3 mb-md-0">
                <div class="time-display-box glass-panel p-4 rounded-4 hover-scale">
                  <span class="d-block text-gold fw-bold mb-2">وقت الإمساك</span>
                  <div class="display-6 fw-bold font-monospace text-theme-base">{{ imsakTime }}</div>
                </div>
              </div>
              <div class="col-md-4 col-sm-6">
                <div class="time-display-box iftar-card p-4 rounded-4 hover-scale">
                  <span class="d-block text-gold fw-bold mb-2">وقت الإفطار</span>
                  <div class="display-6 fw-bold font-monospace text-theme-base">{{ iftarTime }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- مودال تغيير الموقع (location-modal) -->
    <div v-if="showLocationModal" class="manus-modal-overlay animate-fadeIn">
      <div class="manus-modal-container location-modal-improved glass-modal" style="max-width: 800px;">
        <div class="manus-modal-header border-bottom border-light border-opacity-10 pb-3">
          <h4 class="manus-modal-title text-theme-base fw-bold m-0 d-flex align-items-center gap-2">
            <i class="bi bi-geo-alt-fill text-gold"></i> تغيير موقع أوقات الصلاة
          </h4>
          <button class="manus-modal-close bg-transparent border-0 text-theme-muted hover-gold fs-4" @click="showLocationModal = false">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="manus-modal-body py-4">
          <!-- الموقع الحالي -->
          <div class="current-location-display glass-panel mb-4 p-4 rounded-4 text-center">
            <p class="mb-2 text-theme-muted">موقعك الحالي:</p>
            <h3 class="fw-bold text-theme-base mb-0 d-flex justify-content-center align-items-center gap-2">
              <i class="bi bi-pin-map-fill text-gold"></i>
              {{ currentLocation.city }}, {{ currentLocation.country }}
            </h3>
          </div>

          <!-- البحث واستخدام الموقع الحالي -->
          <div class="search-location-wrapper glass-panel rounded-pill mb-4">
            <input
                type="text"
                v-model="locationSearch"
                placeholder="ابحث عن مدينة أو دولة..."
                class="search-input-premium"
            />
            <button class="btn-location-premium" @click="getCurrentLocation">
              <i class="bi bi-crosshair"></i>
              <span>موقعي الحالي</span>
            </button>
          </div>

          <!-- نتائج البحث (قائمة الدول والمدن) -->
          <div class="location-results-list">
            <div v-if="isLoading" class="text-center py-5">
              <div class="spinner-border text-gold" role="status">
                <span class="visually-hidden">جاري البحث...</span>
              </div>
            </div>
            <div v-else class="location-lists row g-4">
              <div class="col-md-6">
                <h6 class="text-gold fw-bold mb-3"><i class="bi bi-globe me-2"></i> الدول ({{ filteredCountries.length }})</h6>
                <div class="list-group location-scroll glass-panel rounded-4 overflow-hidden border-light border-opacity-10 p-2" style="max-height: 250px; overflow-y: auto;">
                  <button
                      v-for="country in filteredCountries"
                      :key="country.code"
                      class="list-group-item list-group-item-action bg-transparent text-theme-base border-0 border-bottom border-light border-opacity-10 py-3 d-flex justify-content-between align-items-center transition-all hover-glass"
                      :class="{ 'bg-white bg-opacity-10 text-gold fw-bold border-gold': selectedCountry?.code === country.code }"
                      @click="selectCountry(country)"
                  >
                    <span>{{ country.name }}</span>
                  </button>
                </div>
              </div>

              <div class="col-md-6">
                <h6 class="text-gold fw-bold mb-3"><i class="bi bi-search me-2"></i> ابحث عن مدينة يدوياً</h6>
                <div class="glass-panel p-4 rounded-4">
                  <div class="form-group mb-4">
                    <label class="form-label text-theme-muted">اسم المدينة (عربي أو إنجليزي)</label>
                    <input
                        type="text"
                        v-model="manualCity"
                        class="form-control glass-input text-theme-base"
                        placeholder="مثال: Khartoum, مكة, London..."
                    />
                  </div>
                  <div class="form-check mb-4 user-select-none">
                    <input class="form-check-input border-gold" type="checkbox" v-model="setAsDefault" id="setDefaultPath">
                    <label class="form-check-label text-theme-muted cursor-pointer ms-2" for="setDefaultPath">
                      حفظ هذا الموقع كإفتراضي
                    </label>
                  </div>
                  <button
                      class="btn btn-gold w-100 py-3 rounded-pill fw-bold shadow-gold"
                      @click="updatePrayerTimesBySearch"
                      :disabled="!manualCity || !selectedCountry || isLoading"
                  >
                    <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                    <i v-else class="bi bi-search me-2"></i> تأكيد البحث والتحديث
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


  </div>
</template>

<script setup>
import { ref, onMounted, computed, reactive, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useToast } from '@/composables/useToast'
import { useAudioPlayer } from '@/composables/useAudioPlayer'
import api from '@/api'
import { useSEO } from '@/composables/useSEO'

import { getFullImageUrl } from '@/utils/imageUrl'


const router = useRouter()
const store = useStore()
const toast = useToast()

const settings = computed(() => store.getters['settings/getSettings'] || {});

// State variables
const showLocationModal = ref(false)
const locationSearch = ref('')
const nextPrayer = ref(null)
const availableCourses = ref([])
const latestArticles = ref([])

const fetchAvailableCourses = async () => {
  try {
    const response = await api.get('/courses', { _silent: true });
    availableCourses.value = response.data.filter(c => c.is_available).slice(0, 3);
  } catch (error) {
    console.error("Error fetching available courses:", error);
  }
}

const fetchLatestArticles = async () => {
  try {
    const response = await api.get('/articles?limit=3', { _silent: true });
    // Handle new paginated format { articles: [], pagination: {} }
    if (response.data && response.data.articles) {
      latestArticles.value = response.data.articles;
    } else {
      latestArticles.value = response.data.slice(0, 3);
    }
  } catch (error) {
    console.error("Error fetching latest articles:", error);
  }
}

// بيانات الميزات
const features = ref([
  {
    icon: 'bi bi-book-half',
    title: 'القرآن الكريم',
    description: 'تلاوة خاشعة وواجهة سهلة للإستماع والتحميل',
    link: '/quran-karim'
  },
  {
    icon: 'bi bi-camera-reels',
    title: 'محتوى هادف',
    description: 'مرئيات إسلامية مفيدة لجميع الأعمار',
    link: '/videos'
  },
  {
    icon: 'bi bi-journal-text',
    title: 'المصحف الشريف',
    description: 'المصحف الشريف كامل للتصفح والقراءة',
    link: '/read-quran'
  }
])

// الإحصائيات
const statistics = ref([
  { id: 1, icon: 'bi bi-people', value: '0', label: 'مستخدم نشط' },
  { id: 2, icon: 'bi bi-play-circle', value: '0', label: 'تفاعل' },
  { id: 3, icon: 'bi bi-book', value: '0', label: 'سورة كريمة' },
  { id: 4, icon: 'bi bi-mic', value: '1', label: 'قارئ معتمد' }
])

const fetchHomeStats = async () => {
  try {
    const response = await api.get('/admin/public-stats', { _silent: true });
    const { users, videos, quran, interactions } = response.data;
    statistics.value[0].value = users.toLocaleString() + '+';
    statistics.value[1].value = interactions.toLocaleString() + '+';
    statistics.value[2].value = (quran || 0).toString();
    // statistics.value[3] remains hardcoded or could be fetched
  } catch (error) {
    console.error("Error fetching home stats:", error);
  }
};

const surahNamesMap = {
  1: "الفاتحة", 2: "البقرة", 3: "آل عمران", 4: "النساء", 5: "المائدة", 6: "الأنعام", 7: "الأعراف", 8: "الأنفال", 9: "التوبة", 10: "يونس",
  11: "هود", 12: "يوسف", 13: "الرعد", 14: "إبراهيم", 15: "الحجر", 16: "النحل", 17: "الإسراء", 18: "الكهف", 19: "مريم", 20: "طه",
  21: "الأنبياء", 22: "الحج", 23: "المؤمنون", 24: "النور", 25: "الفرقان", 26: "الشعراء", 27: "النمل", 28: "القصص", 29: "العنكبوت", 30: "الروم",
  31: "لقمان", 32: "السجدة", 33: "الأحزاب", 34: "سبأ", 35: "فاطر", 36: "يس", 37: "الصافات", 38: "ص", 39: "الزمر", 40: "غافر",
  41: "فصلت", 42: "الشورى", 43: "الزخرف", 44: "الدخان", 45: "الجاثية", 46: "الأحقاف", 47: "محمد", 48: "الفتح", 49: "الحجرات", 50: "ق",
  51: "الذاريات", 52: "الطور", 53: "النجم", 54: "القمر", 55: "الرحمن", 56: "الواقعة", 57: "الحديد", 58: "المجادلة", 59: "الحشر", 60: "الممتحنة",
  61: "الصف", 62: "الجمعـة", 63: "المنافقون", 64: "التغابن", 65: "الطلاق", 66: "التحريم", 67: "الملك", 68: "القلم", 69: "الحاقة", 70: "المعارج",
  71: "نوح", 72: "الجن", 73: "المزمل", 74: "المدثر", 75: "القيامة", 76: "الإنسان", 77: "المرسلات", 78: "النبأ", 79: "النازعات", 80: "عبس",
  81: "التكوير", 82: "الانفطار", 83: "المطـفـفـين", 84: "الانشقاق", 85: "البروج", 86: "الطارق", 87: "الأعلى", 88: "الغاشية", 89: "الفجر", 90: "البلد",
  91: "الشمس", 92: "الليل", 93: "الضحى", 94: "الشرح", 95: "التين", 96: "العلق", 97: "القدر", 98: "البينة", 99: "الزلزلة", 100: "العاديات",
  101: "القارعة", 102: "التكاثر", 103: "العصر", 104: "الهمزة", 105: "الفيل", 106: "قريش", 107: "الماعون", 108: "الكوثر", 109: "الكافرون", 110: "النصر",
  111: "المسد", 112: "الإخلاص", 113: "الفلق", 114: "الناس"
};

const getSurahRealName = (surah) => {
  if (!surah) return "";
  const id = parseInt(surah.number || surah.id);
  return surahNamesMap[id] || surah.name;
};

// بيانات المناسبات
const userEvents = ref([])



// الموقع الحالي
const currentLocation = ref({
  country: 'السعودية',
  city: 'مكة المكرمة',
  latitude: 21.4225,
  longitude: 39.8262,
  timezone: 'Asia/Riyadh'
})

const fetchAllCountries = async () => {
  try {
    const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2');
    const data = await response.json();
    countries.value = data.map(c => ({
      code: c.cca2,
      name: c.name.common
    })).sort((a, b) => a.name.localeCompare(b.name));
  } catch (err) {
    console.error("Error fetching countries:", err);
    // Fallback or keep current list
  }
};

// بيانات الدول والمدن الشاملة باللغة العربية
const countries = ref([
  { code: 'SA', name: 'المملكة العربية السعودية' },
  { code: 'EG', name: 'مصر' },
  { code: 'UAE', name: 'الإمارات العربية المتحدة' },
  { code: 'KWD', name: 'الكويت' },
  { code: 'QAT', name: 'قطر' },
  { code: 'BHR', name: 'البحرين' },
  { code: 'OMN', name: 'عمان' },
  { code: 'YEM', name: 'اليمن' },
  { code: 'JOR', name: 'الأردن' },
  { code: 'LBN', name: 'لبنان' },
  { code: 'SYR', name: 'سوريا' },
  { code: 'IRQ', name: 'العراق' },
  { code: 'PSE', name: 'فلسطين' },
  { code: 'MAR', name: 'المغرب' },
  { code: 'DZA', name: 'الجزائر' },
  { code: 'TUN', name: 'تونس' },
  { code: 'LBY', name: 'ليبيا' },
  { code: 'SDN', name: 'السودان' },
  { code: 'TUR', name: 'تركيا' },
  { code: 'USA', name: 'الولايات المتحدة الأمريكية' },
  { code: 'GBR', name: 'المملكة المتحدة' },
  { code: 'FRA', name: 'فرنسا' },
  { code: 'GER', name: 'ألمانيا' },
  { code: 'MY', name: 'ماليزيا' },
  { code: 'ID', name: 'إندونيسيا' }
])

const updatePrayerTimesForLocation = async (lat, lon) => {
  try {
    const response = await fetch(`https://api.aladhan.com/v1/timings?latitude=${lat}&longitude=${lon}&method=4`);
    const data = await response.json();
    if (data.code === 200) {
      updatePrayerTimesFromAPI(data.data.timings);
      // Attempt to save to local storage if user wants persistence
      localStorage.setItem('userLocation', JSON.stringify({
          latitude: lat,
          longitude: lon,
          city: currentLocation.value.city,
          country: currentLocation.value.country
      }));
    }
  } catch (error) {
    console.error("Prayer times fetch error:", error);
  }
}
const cities = ref([])
const selectedCountry = ref(null)
const selectedCity = ref(null)
const manualCity = ref('')
const isLoading = ref(false)
const setAsDefault = ref(true)

const updatePrayerTimesBySearch = async () => {
  if (!manualCity.value || !selectedCountry.value) {
    toast.add('يرجى اختيار الدولة وإدخال اسم المدينة', 'warning')
    return
  }

  isLoading.value = true
  try {
    const response = await fetch(`https://api.aladhan.com/v1/timingsByCity?city=${manualCity.value}&country=${selectedCountry.value.name}&method=4`)
    const data = await response.json()
    
    if (data.code === 200) {
      currentLocation.value.city = manualCity.value
      currentLocation.value.country = selectedCountry.value.name
      updatePrayerTimesFromAPI(data.data.timings)
      showLocationModal.value = false
      toast.add(`تم تحديث المواقيت لمدينة ${manualCity.value}`, 'success')
      
      if (setAsDefault.value) {
        localStorage.setItem('userLocation', JSON.stringify(currentLocation.value));
      }
    } else {
      toast.add('لم يتم العثور على المدينة، يرجى التأكد من الاسم بالإنجليزية أو العربية الصحيحة', 'error')
    }
  } catch (err) {
    console.error('Search error:', err)
    toast.add('حدث خطأ أثناء البحث عن المدينة', 'error')
  } finally {
    isLoading.value = false
  }
}



// أوقات الصلاة
const prayerTimes = ref([
  { name: 'الفجر', time: '4:30 ص', icon: 'bi bi-sunrise', isNext: false, remaining: '' },
  { name: 'الشروق', time: '5:45 ص', icon: 'bi bi-sun', isNext: false, remaining: '' },
  { name: 'الظهر', time: '12:15 م', icon: 'bi bi-sun', isNext: false, remaining: '' },
  { name: 'العصر', time: '3:45 م', icon: 'bi bi-cloud-sun', isNext: false, remaining: '' },
  { name: 'المغرب', time: '6:30 م', icon: 'bi bi-sunset', isNext: false, remaining: '' },
  { name: 'العشاء', time: '8:00 م', icon: 'bi bi-moon-stars', isNext: false, remaining: '' }
])

const subtractMinutes = (timeStr, mins) => {
    if (!timeStr) return '--:--';
    const parts = timeStr.split(' ');
    if (parts.length < 2) return timeStr;
    const [time, period] = parts;
    let [h, m] = time.split(':').map(Number);
    if (period === 'م' && h !== 12) h += 12;
    if (period === 'ص' && h === 12) h = 0;
    
    let totalMins = h * 60 + m - mins;
    if (totalMins < 0) totalMins += 24 * 60;
    
    let newH = Math.floor(totalMins / 60);
    const newM = totalMins % 60;
    const newPeriod = newH >= 12 ? 'م' : 'ص';
    newH = newH % 12 || 12;
    return `${newH}:${newM.toString().padStart(2, '0')} ${newPeriod}`;
};

const imsakTime = computed(() => {
    const fajr = prayerTimes.value.find(p => p.name === 'الفجر')?.time;
    return subtractMinutes(fajr, 10);
});

const iftarTime = computed(() => {
    return prayerTimes.value.find(p => p.name === 'المغرب')?.time || '--:--';
});

// فلتر للبحث
const filteredCountries = computed(() => {
  if (!locationSearch.value) return countries.value
  return countries.value.filter(country =>
      country.name.toLowerCase().includes(locationSearch.value.toLowerCase())
  )
})

const sudanCities = [
  { name: 'الخرطوم', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'أم درمان', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'الخرطوم بحري', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'مروي', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'دنقلا', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'شندي', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'بورتسودان', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'كسلا', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'الأبيض', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'نيالا', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'ود مدني', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'القضارف', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'الفاشر', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'الدمازين', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'الجنينة', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'ربك', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'سنار', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'الدويم', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'عطبرة', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'الدامر', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'المناقل', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'النهود', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'حلفا', countryCode: 'SDN', countryName: 'السودان' },
  { name: 'كوستي', countryCode: 'SDN', countryName: 'السودان' }
];

// Initialize cities with Sudan cities
cities.value = sudanCities;

const filteredCities = computed(() => {
  const filtered = selectedCountry.value
      ? cities.value.filter(city => city.countryCode === selectedCountry.value.code)
      : cities.value

  if (!locationSearch.value) return filtered
  return filtered.filter(city =>
      city.name.toLowerCase().includes(locationSearch.value.toLowerCase())
  )
})



// دوال الموقع
const selectCountry = (country) => {
  selectedCountry.value = country
}

const getCurrentLocation = async (silent = false) => {
  isLoading.value = true;
  
  // Try Browser Geolocation First
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        await updateLocationAndPrayers(latitude, longitude, silent);
      },
      async (error) => {
        console.warn('Browser Geolocation failed, trying IP fallback...', error);
        await ipLocationFallback(silent);
      },
      { timeout: 5000 }
    );
  } else {
    await ipLocationFallback(silent);
  }
};

const ipLocationFallback = async (silent) => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    if (data.latitude && data.longitude) {
      await updateLocationAndPrayers(data.latitude, data.longitude, silent);
    }
  } catch (err) {
    console.error('IP Geolocation failed:', err);
    if (!silent) toast.add('تعذر تحديد موقعك تلقائياً، يرجى اختيار المدينة يدوياً.', 'error');
    isLoading.value = false;
  }
};

const updateLocationAndPrayers = async (lat, lon, silent) => {
  try {
    currentLocation.value.latitude = lat;
    currentLocation.value.longitude = lon;

    // 1. Fetch Prayer Times
    const prayerP = fetch(`https://api.aladhan.com/v1/timings?latitude=${lat}&longitude=${lon}&method=4`);
    
    // 2. Fetch City Name (Reverse Geocode)
    const geoP = fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=ar`);

    const [pRes, gRes] = await Promise.all([prayerP, geoP]);
    const pData = await pRes.json();
    const gData = await gRes.json();

    if (pData.code === 200) {
      updatePrayerTimesFromAPI(pData.data.timings);
      currentLocation.value.timezone = pData.data.meta.timezone;

      if (gData && gData.address) {
        currentLocation.value.city = gData.address.city || gData.address.town || gData.address.village || gData.address.state || 'موقعك';
        currentLocation.value.country = gData.address.country || '';
      }

      // Save to local storage
      localStorage.setItem('alrehima_user_location', JSON.stringify(currentLocation.value));

      if (!silent) toast.add('تم تحديث موقعك ومواقيت الصلاة بدقة!', 'success');
    }
  } catch (err) {
    console.error('Update location error:', err);
  } finally {
    isLoading.value = false;
    showLocationModal.value = false;
  }
};

const updatePrayerTimesFromAPI = (timings) => {
  prayerTimes.value = [
    { name: 'الفجر', time: formatTo12Hour(timings.Fajr), icon: 'bi bi-sunrise', isNext: false, remaining: '' },
    { name: 'الشروق', time: formatTo12Hour(timings.Sunrise), icon: 'bi bi-sun', isNext: false, remaining: '' },
    { name: 'الظهر', time: formatTo12Hour(timings.Dhuhr), icon: 'bi bi-sun', isNext: false, remaining: '' },
    { name: 'العصر', time: formatTo12Hour(timings.Asr), icon: 'bi bi-cloud-sun', isNext: false, remaining: '' },
    { name: 'المغرب', time: formatTo12Hour(timings.Maghrib), icon: 'bi bi-sunset', isNext: false, remaining: '' },
    { name: 'العشاء', time: formatTo12Hour(timings.Isha), icon: 'bi bi-moon-stars', isNext: false, remaining: '' }
  ]
  calculateNextPrayer()
}

const formatTo12Hour = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number)
  const period = hours >= 12 ? 'م' : 'ص'
  const hours12 = hours % 12 || 12
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`
}

const updateLocation = () => {
  if (selectedCity.value && selectedCountry.value) {
    currentLocation.value.city = selectedCity.value.name
    currentLocation.value.country = selectedCity.value.countryName

    // تحديث أوقات الصلاة للموقع الجديد
    updatePrayerTimesForLocation(currentLocation.value.latitude, currentLocation.value.longitude)

    showLocationModal.value = false
  }
}

// حساب الصلاة القادمة والوقت المتبقي
const calculateNextPrayer = () => {
  const now = new Date()
  const currentTime = now.getHours() * 60 + now.getMinutes()

  let nextPrayerIndex = -1
  let minTimeDiff = Infinity

  // تحويل أوقات الصلاة إلى دقائق من بداية اليوم
  prayerTimes.value.forEach((prayer, index) => {
    const prayerTimeStr = prayer.time
    const [timePart, period] = prayerTimeStr.split(' ')
    const [hours, minutes] = timePart.split(':').map(Number)

    let prayerMinutes = hours * 60 + minutes

    // تحويل إلى نظام 24 ساعة
    if (period === 'م' && hours !== 12) {
      prayerMinutes += 12 * 60
    } else if (period === 'ص' && hours === 12) {
      prayerMinutes -= 12 * 60
    }

    // حساب الفرق الزمني
    let timeDiff = prayerMinutes - currentTime

    // إذا كانت الصلاة في اليوم التالي
    if (timeDiff < 0) {
      timeDiff += 24 * 60
    }

    // تحديث أقرب صلاة
    if (timeDiff < minTimeDiff) {
      minTimeDiff = timeDiff
      nextPrayerIndex = index
    }
  })

  // تحديث حالة الصلاة القادمة
  prayerTimes.value.forEach((prayer, index) => {
    prayer.isNext = (index === nextPrayerIndex)

    if (prayer.isNext) {
      // حساب الوقت المتبقي
      const hoursRemaining = Math.floor(minTimeDiff / 60)
      const minutesRemaining = minTimeDiff % 60

      prayer.remaining = `${hoursRemaining} ساعة و ${minutesRemaining} دقيقة`
      nextPrayer.value = prayer
    } else {
      prayer.remaining = ''
    }
  })
}

const fetchEvents = async () => {
  try {
    const response = await api.get('/occasions', { _silent: true });
    userEvents.value = response.data;
  } catch (err) {
    console.error('Error fetching events:', err);
  }
};


// Audio playback — uses the shared global player (no conflict with QuranAlkarim page)
const {
  currentSurah: currentPlayingSurahRef,
  isPlaying,
  currentTime,
  duration,
  progress,
  playSurah,
  seekTo,
  formatTime,
} = useAudioPlayer();

// Alias for template compatibility
const currentPlayingSurah = currentPlayingSurahRef;

// Toggle play/pause for a surah (used in Home page list)
const togglePlaySurah = async (surah) => {
  try {
    await playSurah(surah);
  } catch (error) {
    console.error('Play error:', error);
    toast.add('تعذر تشغيل هذه السورة.', 'error');
  }
}

// Seek audio by clicking on progress bar
const seekAudio = (event) => {
  if (!duration.value) return;
  const rect = event.currentTarget.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const percentage = x / rect.width;
  seekTo(percentage * duration.value);
}

const navigateToQuranSection = () => {
  router.push({ path: '/quran-karim', query: { play: '1' } });
}

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

// عند تحميل المكون
onMounted(async () => {
  // جلب كافة البيانات في وقت واحد (Parallel) لتقليل وقت التحميل
  Promise.all([
    store.dispatch('settings/fetchPublicSettings'),
    fetchHomeStats(),
    fetchLatestArticles(),
    fetchAllCountries(),
    fetchEvents(),
    fetchAvailableCourses()
  ]).catch(err => console.error("Initial data fetch error:", err));

  // Load saved location from localStorage if exists
  const savedLoc = localStorage.getItem('alrehima_user_location');
  if (savedLoc) {
    try {
      const parsed = JSON.parse(savedLoc);
      currentLocation.value = parsed;
      // Refresh prayers for that location (Non-blocking)
      updateLocationAndPrayers(parsed.latitude, parsed.longitude, true).catch(err => console.error(err));
    } catch (e) {
      getCurrentLocation(true);
    }
  } else {
    // Force get fresh location using IP or Geolocation first (silent mode)
    getCurrentLocation(true);
  }

  // تحديث وقت الصلاة التالية كل دقيقة
  setInterval(() => {
    calculateNextPrayer()
  }, 60000)

  // حساب أولي للصلاة التالية
  calculateNextPrayer()
})
</script>

<style scoped>
/* Base Dark Theme Overrides */
.home-luxury {
  color: var(--text-color);
  overflow-x: hidden;
  background-color: var(--bg-color); /* Matches theme */
}

/* Background Effects Similar to Videos/Quran pages */
.home-luxury::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, var(--nav-bg) 0%, transparent 70%);
  z-index: 0;
  pointer-events: none;
}

/* Common Premium Classes */
.text-gold { color: #FFD700 !important; }
.bg-gold { background-color: #FFD700 !important; }
.border-gold { border-color: rgba(255, 215, 0, 0.3) !important; }
.shadow-gold { box-shadow: 0 4px 15px rgba(255, 215, 0, 0.15) !important; }
.text-shadow-glow { text-shadow: 0 0 20px rgba(255, 215, 0, 0.4); }
.hover-scale { transition: transform 0.3s; }
.hover-scale:hover { transform: translateY(-5px); }
.hover-white:hover { color: #fff !important; transition: 0.3s; }
.hover-glass:hover { background-color: rgba(255,255,255,0.05) !important; }

/* Glassmorphism Containers */
.glass-panel {
  background: rgba(30, 30, 45, 0.5);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.glass-card {
  background: rgba(25, 25, 35, 0.6);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.glass-card:hover {
  border-color: rgba(255, 215, 0, 0.3);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4), 0 0 15px rgba(255, 215, 0, 0.1);
  background: rgba(30, 30, 45, 0.8);
}

/* Animated Glow for feature cards */
.card-glow {
  position: absolute;
  top: -60px;
  right: -60px;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(255, 215, 0, 0.15) 0%, transparent 70%);
  border-radius: 50%;
  opacity: 0;
  transition: 0.5s;
  pointer-events: none;
}
.glass-card:hover .card-glow { opacity: 1; transform: scale(1.5); }
.card-glow.active { opacity: 1; background: radial-gradient(circle, rgba(255, 215, 0, 0.25) 0%, transparent 70%); animation: pulseBg 2s infinite alternate;}

.glass-badge {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
}

.glass-input {
  background: rgba(0, 0, 0, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: white !important;
}
.glass-input:focus {
  border-color: rgba(255, 215, 0, 0.5) !important;
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.2) !important;
}

/* Modals matching Premium Theme */
.glass-modal {
  background: var(--card-bg) !important;
  backdrop-filter: blur(25px) !important;
  -webkit-backdrop-filter: blur(25px) !important;
  border: 1px solid var(--border-color) !important;
  color: var(--text-color);
  border-radius: 28px !important;
  box-shadow: 0 40px 100px rgba(0,0,0,0.3) !important;
  overflow: hidden;
}

[data-theme="light"] .glass-modal {
  background: rgba(255, 255, 255, 0.9) !important;
  border-color: rgba(0, 0, 0, 0.05) !important;
}

/* Animations */
.animate-slide-up { animation: slideUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out both; }
.animate-pulse { animation: pulse 2s infinite; }

@keyframes slideUp { 0% { opacity: 0; transform: translateY(30px); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes fadeIn { 0% { opacity: 0; } 100% { opacity: 1; } }
@keyframes pulse { 0% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.1); opacity: 1; text-shadow: 0 0 20px rgba(255,215,0,0.5);} 100% { transform: scale(1); opacity: 0.8;} }
@keyframes pulseBg { from { opacity: 0.5; transform: scale(1); } to { opacity: 1; transform: scale(1.2); } }

/* Section Specific Styling Overrides */
.hero-section {
  padding: 10rem 0 6rem;
  background: transparent;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 600" opacity="0.1"><path d="M0 0h1200v600H0z" fill="none"/><path d="M0 300c120-60 240-60 360 0s240 60 360 0 240-60 360 0 240 60 360 0" stroke="%23d4af37" fill="none"/></svg>') center/cover;
}

.hero-content {
  position: relative;
  z-index: 2;
}

.btn-gold {
  background: linear-gradient(135deg, #FFD700 0%, #B8860B 100%);
  color: #000 !important;
  border: none;
  transition: all 0.3s;
}
.btn-gold:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(255, 215, 0, 0.4);
}

.btn-glass-outline {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 215, 0, 0.4);
  color: #fff;
  transition: all 0.3s;
}
.btn-glass-outline:hover {
  background: rgba(255, 215, 0, 0.1);
  border-color: #FFD700;
  color: #FFD700;
  transform: translateY(-2px);
}

/* Titles */
.section-title::after {
  content: '';
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(to right, transparent, #FFD700, transparent);
  border-radius: 50%;
}

/* Custom progress for quran */
.progress-bar { transition: width 0.3s ease; }

.feature-card:hover {
  transform: translateY(-10px);
  border-color: rgba(212, 175, 55, 0.3);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
}

.feature-icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #1a1a2e 0%, #6828d1 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  border: 2px solid #d4af37;
}

.feature-icon-wrapper i {
  font-size: 2.5rem;
  color: #d4af37;
}

.feature-title {
  font-size: 1.5rem;
  color: var(--text-color);
  margin-bottom: 15px;
  font-weight: 600;
}

.feature-card p {
  color: var(--text-muted);
  margin-bottom: 20px;
  line-height: 1.6;
}

.feature-link {
  color: #d4af37;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-weight: 500;
  transition: gap 0.3s ease;
}

.feature-link:hover {
  gap: 10px;
}

/* قسم المصحف */
.quran-section {
  background: var(--nav-bg);
}

.quran-player {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(212, 175, 55, 0.1);
}

.player-header {
  background: rgba(0, 0, 0, 0.2);
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.player-header h4 {
  color: #d4af37;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.player-controls {
  display: flex;
  gap: 10px;
}

.player-btn {
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid #d4af37;
  color: #d4af37;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.player-btn:hover {
  background: rgba(212, 175, 55, 0.2);
  transform: scale(1.1);
}

.play-btn {
  background: #d4af37;
  color: #1a1a2e;
}

.player-body {
  padding: 20px;
}

.surah-info h5 {
  color: var(--text-color);
  margin-bottom: 5px;
  font-size: 1.3rem;
}

.surah-info p {
  color: var(--text-muted);
  margin-bottom: 20px;
}

.progress-section {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}

.progress-time {
  color: #d4af37;
  font-size: 0.9rem;
  min-width: 40px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #d4af37, #f7ef8a);
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* قائمة السور */
.surah-list {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 20px;
  height: 100%;
  border: 1px solid rgba(212, 175, 55, 0.1);
}

.surah-list-title {
  color: #d4af37;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.3s ease;
}

.list-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.item-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.surah-number {
  background: #d4af37;
  color: #1a1a2e;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.item-info h6 {
  color: var(--text-color);
  margin: 0;
  font-size: 1.1rem;
}

.item-info p {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.9rem;
}

.play-surah-btn {
  background: transparent;
  border: none;
  color: #d4af37;
  font-size: 2rem;
  cursor: pointer;
  transition: transform 0.3s ease, color 0.3s ease;
}

.play-surah-btn:hover {
  transform: scale(1.2);
  color: #f7ef8a;
}

.play-surah-btn i {
  filter: drop-shadow(0 2px 4px rgba(212, 175, 55, 0.3));
}

.current-playing-info {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  border: 1px solid rgba(212, 175, 55, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

[data-theme='light'] .current-playing-info {
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(212, 175, 55, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}

.progress-bar-custom {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  cursor: pointer;
  overflow: hidden;
  position: relative;
}

[data-theme='light'] .progress-bar-custom {
  background: rgba(0, 0, 0, 0.05);
}

.progress-fill-custom {
  height: 100%;
  background: linear-gradient(90deg, #d4af37, #f7ef8a);
  border-radius: 3px;
  transition: width 0.3s ease;
  position: relative;
}

.progress-fill-custom::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* الإحصائيات */
.stats-section {
  background: var(--nav-bg);
}

.stat-item {
  padding: 30px;
}

.stat-icon {
  width: 80px;
  height: 80px;
  background: rgba(212, 175, 55, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  border: 2px solid rgba(212, 175, 55, 0.3);
}

.stat-icon i {
  font-size: 2.5rem;
  color: #d4af37;
}

.stat-number {
  font-size: 3.5rem;
  color: #d4af37;
  margin-bottom: 5px;
  font-weight: 800;
  text-shadow: 0 4px 10px rgba(0,0,0,0.3);
}

.stat-item p {
  font-size: 1.2rem;
  color: var(--text-color) !important;
  font-weight: 600;
  opacity: 0.9;
}

.view-count {
  font-size: 0.75rem;
  color: #ffc107;
  background: rgba(255, 193, 7, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
}

.item-info .gap-2 {
  margin-top: 4px;
}

/* المناسبات */
.events-section {
  position: relative;
  z-index: 1;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.btn-add-event {
  background: #d4af37;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-add-event:hover {
  background: #b8941f;
  transform: translateY(-2px);
}

.empty-events {
  text-align: center;
  padding: 50px 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
}

.empty-icon {
  font-size: 4rem;
  color: #d4af37;
  margin-bottom: 20px;
}

.empty-events h4 {
  color: var(--text-color);
  margin-bottom: 15px;
  font-weight: 700;
}

.empty-events p {
  color: var(--text-muted);
  margin-bottom: 0;
  line-height: 1.6;
}

.events-timeline {
  max-width: 800px;
  margin: 0 auto;
}

/* المناسبات الإسلامية */
.events-section {
  background: transparent;
}

.event-item {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  border-radius: 15px;
  padding: 25px;
  margin-bottom: 20px;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.event-item:hover {
  transform: translateY(-8px) scale(1.01);
  background: rgba(255, 255, 255, 0.06) !important;
  border-color: rgba(255, 215, 0, 0.3) !important;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
}

.event-date {
  background: linear-gradient(135deg, #FFD700 0%, #B8860B 100%) !important;
  color: #0b061d !important;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  min-width: 130px;
  margin-left: 20px;
  box-shadow: 0 10px 25px rgba(255, 215, 0, 0.15);
  transition: transform 0.3s;
}

.event-item:hover .event-date {
  transform: rotate(-3deg);
}

.event-day {
  display: block;
  font-size: 2.8rem;
  font-weight: 800;
  color: #0b061d !important;
  line-height: 1;
}

.event-month {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0b061d !important;
  opacity: 0.9;
}

.event-content {
  flex: 1;
}

.event-content h5 {
  color: var(--text-color) !important;
  margin-bottom: 10px;
  font-size: 1.5rem !important;
  font-weight: 700;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.1);
}

.hero-glow-text {
  color: var(--text-color);
  text-shadow: 0 0 20px rgba(255, 215, 0, 0.3),
  0 0 40px rgba(255, 215, 0, 0.2);
}

.hero-subtitle {
  color: var(--text-muted);
}

.event-content p {
  color: var(--text-muted) !important;
  margin-bottom: 15px;
  line-height: 1.6;
  font-size: 1.05rem;
}

.event-meta {
  display: flex;
  gap: 20px;
  color: rgba(255, 215, 0, 0.7);
  font-size: 0.95rem;
  flex-wrap: wrap;
}

.event-meta i {
  margin-left: 5px;
}

.online-badge {
  background: rgba(46, 125, 50, 0.1) !important;
  color: #4ade80 !important;
  border: 1px solid rgba(46, 125, 50, 0.15);
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.online-badge.offline {
  background: rgba(37, 99, 235, 0.1) !important;
  color: #60a5fa !important;
  border: 1px solid rgba(37, 99, 235, 0.15);
}

.btn-event-delete {
  background: rgba(255, 0, 0, 0.1);
  color: #ff4d4d;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 10px;
}

.btn-event-delete:hover {
  background: rgba(255, 0, 0, 0.2);
  transform: scale(1.1);
}

/* أوقات الصلاة */
.prayer-times {
  background: var(--nav-bg);
  position: relative;
}

.prayer-grid {
  max-width: 1100px;
  margin: 0 auto;
}

.prayer-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  cursor: default;
}

.prayer-card:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 215, 0, 0.1);
}

.active-prayer {
  background: rgba(255, 215, 0, 0.06) !important;
  border: 1px solid rgba(255, 215, 0, 0.2) !important;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

.active-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, transparent, #FFD700, transparent);
  box-shadow: 0 0 15px #FFD700;
}

.prayer-icon-container {
  width: 55px;
  height: 55px;
  background: rgba(255, 215, 0, 0.05);
  border: 1px solid rgba(255, 215, 0, 0.1);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFD700;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.prayer-icon-container.active {
  background: linear-gradient(135deg, #FFD700 0%, #B8860B 100%);
  color: #0b061d;
  box-shadow: 0 8px 25px rgba(255, 215, 0, 0.4);
  transform: rotate(-5deg);
}

.prayer-name {
  font-size: 1rem;
  letter-spacing: 0.5px;
}

.prayer-time {
  color: var(--text-color);
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.next-label .badge {
  font-size: 0.7rem;
  letter-spacing: 1px;
}

.location-info {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  max-width: 90%;
}
@media (min-width: 768px) {
  .location-info {
    max-width: fit-content;
  }
}

.hero-actions .btn-outline-gold {
  border: 2px solid var(--accent-gold);
  color: var(--text-color);
  background: transparent;
}

.hero-actions .btn-outline-gold:hover {
  background: rgba(255, 215, 0, 0.1);
  color: var(--text-color);
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.2);
}

.btn-location {
  background: rgba(255, 215, 0, 0.1);
  color: #FFD700;
  border: 1px solid rgba(255, 215, 0, 0.2);
  padding: 10px 24px;
  border-radius: 30px;
  transition: all 0.3s ease;
}

.btn-location:hover {
  background: #FFD700;
  color: #0b061d;
  transform: scale(1.05);
}

.next-prayer-badge {
  background: #FFD700 !important;
  color: #0b061d !important;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.85rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.2);
}

@media (max-width: 768px) {
  .next-prayer-badge {
    font-size: 0.75rem;
    padding: 6px 15px;
  }
}

.city-prayer-time {
  font-size: 0.8rem;
  color: #666;
}

.city-prayer-time {
  font-size: 0.8rem;
  color: #666;
}

/* -------------------------------------------------- */
/* أنماط المودالات المحسّنة (Improved Modals Styles) */
/* -------------------------------------------------- */

/* الأنماط المنسقة للخرائط (Location Modal) */
.manus-modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 20px;
}

.manus-modal-container {
  width: 100%;
  max-width: 850px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.manus-modal-header {
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 215, 0, 0.03);
}

.manus-modal-body {
  padding: 2rem;
  overflow-y: auto;
}

.current-location-display {
  background: linear-gradient(135deg, var(--accent-gold-transparent) 0%, transparent 100%);
  border: 1px solid var(--border-color);
  border-radius: 24px;
  position: relative;
  overflow: hidden;
}

.current-location-display::before {
  content: '';
  position: absolute;
  top: 0; right: 0;
  width: 100px; height: 100px;
  background: radial-gradient(circle, var(--accent-gold-transparent) 0%, transparent 70%);
  opacity: 0.5;
}

.search-location-wrapper {
  display: flex;
  align-items: center;
  padding: 6px;
  gap: 10px;
  border: 1px solid var(--border-color);
}

.search-input-premium {
  flex: 1;
  background: transparent;
  border: none;
  padding: 10px 20px;
  color: var(--text-color);
  font-size: 1rem;
}

.search-input-premium:focus { outline: none; }

.btn-location-premium {
  background: var(--accent-gold);
  color: #111;
  border: none;
  padding: 10px 20px;
  border-radius: 50px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: 0.3s;
}

.btn-location-premium:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px var(--accent-gold-transparent);
}

.location-scroll button {
  transition: 0.2s;
  border-bottom: 1px solid var(--border-color) !important;
}

.location-scroll button:hover {
  background: var(--accent-gold-transparent) !important;
  color: var(--accent-gold) !important;
}

.location-scroll button.bg-white.bg-opacity-10 {
  background: var(--accent-gold) !important;
  color: #111 !important;
  font-weight: 800 !important;
}

.manus-modal-close {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
}

.manus-modal-close:hover {
  background: rgba(255, 0, 0, 0.1);
  color: #ff4d4d !important;
}

/* Scrollbar */
::-webkit-scrollbar { width: 8px; }
::-webkit-scrollbar-track { background: #1a1a1a; }
::-webkit-scrollbar-thumb { background: #FFD700; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #B8860B; }

/* Ramadan Section Styling */
.ramadan-card {
  background: linear-gradient(135deg, #1f1a43 0%, #2e1a4c 100%);
  border: 1px solid rgba(255, 215, 0, 0.2);
}

.ramadan-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at center, rgba(255, 215, 0, 0.1) 0%, transparent 70%);
  z-index: 0;
  pointer-events: none;
}

.time-display-box {
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.time-display-box:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  transform: translateY(-5px);
}

.iftar-card {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(184, 134, 11, 0.05) 100%);
  border: 1px solid rgba(255, 215, 0, 0.3);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(255, 215, 0, 0.1);
}

.border-gold {
  border: 1px solid rgba(255, 215, 0, 0.4) !important;
}

.text-gold {
  color: #FFD700 !important;
}

.backdrop-blur {
  backdrop-filter: blur(10px);
}

/* تصميم متجاوب */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .hero-actions {
    flex-direction: column;
    align-items: center;
  }

  .section-title {
    font-size: 2rem;
  }

  .section-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }

  .event-item {
    flex-direction: column;
    text-align: center;
  }

  .event-date {
    margin-left: 0;
    margin-bottom: 20px;
  }

  .btn-event-delete {
    margin-right: 0;
    margin-top: 15px;
  }

  .location-lists {
    grid-template-columns: 1fr;
    height: 400px;
  }

  .manus-modal-container {
    max-width: 95%;
  }

  .manus-modal-actions {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .prayer-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .location-lists {
    grid-template-columns: 1fr;
  }

  .location-info {
    flex-direction: column;
    gap: 5px;
  }
}
</style>
